// @flow

import loadGlyphRange from '../style/load_glyph_range';

import TinySDF from '@mapbox/tiny-sdf';
import isChar from '../util/is_char_in_unicode_block';
import {asyncAll} from '../util/util';
import {AlphaImage} from '../util/image';

import type {StyleGlyph} from '../style/style_glyph';
import type {RequestManager} from '../util/mapbox';
import type {Callback} from '../types/callback';

type Entry = {
    // null means we've requested the range, but the glyph wasn't included in the result.
    glyphs: {[id: number]: StyleGlyph | null},
    requests: {[range: number]: Array<Callback<{[_: number]: StyleGlyph | null}>>},
    ranges: {[range: number]: boolean | null},
    tinySDF?: TinySDF
};

class GlyphManager {
    requestManager: RequestManager;
    localIdeographFontFamily: ?string;
    entries: {[_: string]: Entry};
    url: ?string;

    // exposed as statics to enable stubbing in unit tests
    static loadGlyphRange: typeof loadGlyphRange;
    static TinySDF: Class<TinySDF>;

    constructor(requestManager: RequestManager, localIdeographFontFamily: ?string) {
        this.requestManager = requestManager;
        this.localIdeographFontFamily = localIdeographFontFamily;
        this.entries = {};
        /*
         * feat(8330): WebClient-组件库升级Vue3与优化
         * 修改说明: 增加字体支持类型的状态
         * 版权所有: 武汉中地数码科技有限公司
         * 修改人: 赵凯 2025-07-07
         */
        // 增加一个字体支持类型的状态。'client'表示使用sdf贴图，本地文字处理；'server'使用服务端请求的字体; 字体支持类型默认为undefined，这个时候需要通过请求字体来判断字体支持类型的状态，如果服务端支持，则改为'server',反之改为'client'
        this.supportFonts = {};
    }

    setURL(url: ?string) {
        this.url = url;
        /*
         * feat(8330): WebClient-组件库升级Vue3与优化
         * 修改说明: 修改字体服务地址后，清空缓存的文字纹理
         * 版权所有: 武汉中地数码科技有限公司
         * 修改人: 赵凯 2025-07-07
         */
        this.entries = {};
        this.supportFonts = {};
    }

    getGlyphs(glyphs: {[stack: string]: Array<number>}, callback: Callback<{[stack: string]: {[id: number]: ?StyleGlyph}}>) {
        const all = [];
        for (const stack in glyphs) {
            for (const id of glyphs[stack]) {
                all.push({stack, id});
            }
        }

        asyncAll(all, ({stack, id}, callback: Callback<{stack: string, id: number, glyph: ?StyleGlyph}>) => {
            let entry = this.entries[stack];
            if (!entry) {
                entry = this.entries[stack] = {
                    glyphs: {},
                    requests: {},
                    ranges: {}
                };
            }

            let glyph = entry.glyphs[id];
            if (glyph !== undefined) {
                callback(null, {stack, id, glyph});
                return;
            }

            glyph = this._tinySDF(entry, stack, id);
            if (glyph) {
                entry.glyphs[id] = glyph;
                callback(null, {stack, id, glyph});
                return;
            }

            const range = Math.floor(id / 256);
            if (range * 256 > 65535) {
                callback(new Error('glyphs > 65535 not supported'));
                return;
            }

            if (entry.ranges[range]) {
                callback(null, {stack, id, glyph});
                return;
            }

            let requests = entry.requests[range];
            if (!requests) {
                requests = entry.requests[range] = [];
                GlyphManager.loadGlyphRange(stack, range, (this.url: any), this.requestManager,
                    (err, response: ?{[_: number]: StyleGlyph | null}) => {
                        if (response) {
                            for (const id in response) {
                                if (!this._doesCharSupportLocalGlyph(+id)) {
                                    entry.glyphs[+id] = response[+id];
                                }
                            }
                            entry.ranges[range] = true;
                            /*
                             * feat(8330): WebClient-组件库升级Vue3与优化
                             * 修改说明: 修改支持的字体状态，如果是server，则优先使用提供的字体服务
                             * 版权所有: 武汉中地数码科技有限公司
                             * 修改人: 赵凯 2025-07-07
                             */
                            if (stack) {
                                this.supportFonts[stack] = "server";
                                const fonts = stack.split(",");
                                fonts.forEach((font) => {
                                    this.supportFonts[font] = "server";
                                });
                            }
                        } else {
                            this.supportFonts[stack] = "client";
                        }
                        for (const cb of requests) {
                            cb(err, response);
                        }
                        delete entry.requests[range];
                    });
            }

            requests.push((err, result: ?{[_: number]: StyleGlyph | null}) => {
                if (err) {
                    /*
                     * feat(8330): WebClient-组件库升级Vue3与优化
                     * 修改说明: 当字体请求不成功时，客户端生成文字纹理
                     * 版权所有: 武汉中地数码科技有限公司
                     * 修改人: 赵凯 2025-07-07
                     */
                    if (!this.localIdeographFontFamily) {
                        console.error(
                        `当前字体服务不支持字体'${stack}'，请更换字体或更换字体库`
                        );
                    }
                    const glyph = this._tinySDF(entry, stack, id) || null;
                    entry.glyphs[id] = glyph;
                    callback(null, {stack, id, glyph});
                } else if (result) {
                    callback(null, {stack, id, glyph: result[id] || null});
                }
            });
        }, (err, glyphs: ?Array<{stack: string, id: number, glyph: ?StyleGlyph}>) => {
            if (err) {
                callback(err);
            } else if (glyphs) {
                const result = {};

                for (const {stack, id, glyph} of glyphs) {
                    // Clone the glyph so that our own copy of its ArrayBuffer doesn't get transferred.
                    (result[stack] || (result[stack] = {}))[id] = glyph && {
                        id: glyph.id,
                        bitmap: glyph.bitmap.clone(),
                        metrics: glyph.metrics
                    };
                }

                callback(null, result);
            }
        });
    }

    _doesCharSupportLocalGlyph(id: number): boolean {
        /* eslint-disable new-cap */
        return !!this.localIdeographFontFamily &&
            (isChar['CJK Unified Ideographs'](id) ||
                isChar['Hangul Syllables'](id) ||
                isChar['Hiragana'](id) ||
                isChar['Katakana'](id));
        /* eslint-enable new-cap */
    }

    _tinySDF(entry: Entry, stack: string, id: number): ?StyleGlyph {
        const family = this.localIdeographFontFamily;
        if (!family) {
            return;
        }

        /*
         * feat(8330): WebClient-组件库升级Vue3与优化
         * 修改说明: 判断是否使用字体服务。1、非默认的url 2、localIdeographFontFamily为false或者不在字符范围内 3、支持字体类型为空或者支持字体类型为'server'
         * 版权所有: 武汉中地数码科技有限公司
         * 修改人: 赵凯 2025-07-07
         */
        /**
         * localIdeographFontFamily的语义
         * 用于指定一个 CSS 字体族（font-family），以本地覆盖某些字符范围的字形生成。
            这些字符范围包括：
            1.CJK Unified Ideographs（中日韩统一表意文字）
            2.Hiragana（平假名）
            3.Katakana（片假名）
            4.Hangul Syllables（朝鲜文音节）
         */
        if (
            !this._doesCharSupportLocalGlyph(id) &&
            this.url !== GlyphManager.DEFAULT_GLYPH_URL &&
            (!this.supportFonts[stack] || this.supportFonts[stack] === "server")
        ) {
            return;
        }

        let tinySDF = entry.tinySDF;
        if (!tinySDF) {
            let fontWeight = '400';
            if (/bold/i.test(stack)) {
                fontWeight = '900';
            } else if (/medium/i.test(stack)) {
                fontWeight = '500';
            } else if (/light/i.test(stack)) {
                fontWeight = '200';
            }
            tinySDF = entry.tinySDF = new GlyphManager.TinySDF(
                24,
                3,
                8,
                0.25,
                stack,
                fontWeight
            );
        }

        return {
            id,
            bitmap: new AlphaImage({width: 30, height: 30}, tinySDF.draw(String.fromCharCode(id))),
            metrics: {
                width: 24,
                height: 24,
                left: 0,
                top: -8,
                advance: 24
            }
        };
    }
}

GlyphManager.loadGlyphRange = loadGlyphRange;
GlyphManager.TinySDF = TinySDF;
/*
* feat(8330): WebClient-组件库升级Vue3与优化
* 修改说明: 增加默认字体url，防止语法报错
* 版权所有: 武汉中地数码科技有限公司
* 修改人: 赵凯 2025-07-07
*/
GlyphManager.DEFAULT_GLYPH_URL =
    "/fonts/{fontstack}?range={range}.pbf&f=pbf";

export default GlyphManager;
